/**
 * Funnel renderer — segment-builder card.
 *
 * The AI uses this when it's narrating a multi-step audience build:
 *   step 1: all R senators (50)
 *   step 2: who voted yea on KOSA (38)
 *   step 3: who haven't been touched in 30d (14)
 *
 * Each step gets a horizontal bar that visually narrows as the count
 * shrinks. Optional save_as_segment_label promotes a CTA the user can
 * click to persist the final segment (the actual save action is wired
 * by the host app).
 */

import { registerRenderer, type RendererProps } from "./registry.js";
import { Filter, Save } from "lucide-react";

export interface FunnelStep {
  label?: string;
  filter_applied?: string;
  count?: number;
}

export interface FunnelInput {
  title?: string;
  steps?: FunnelStep[];
  save_as_segment_label?: string;
}

const STEP_BG: string[] = [
  "hsl(145 22% 95%)",
  "hsl(145 22% 92%)",
  "hsl(145 22% 89%)",
  "hsl(145 22% 86%)",
  "hsl(145 22% 83%)",
  "hsl(145 22% 80%)",
];
const STEP_WIDTHS = ["100%", "92%", "84%", "76%", "68%", "60%"];

function FunnelCard({ input, onConsume }: RendererProps<FunnelInput>) {
  const steps = Array.isArray(input.steps) ? input.steps : [];

  const handleSave = () => {
    if (!input.save_as_segment_label) return;
    onConsume?.({
      kind: "save_segment",
      payload: {
        label: input.save_as_segment_label,
        steps,
      },
      followupMessage: `Save the segment as "${input.save_as_segment_label}". The funnel was: ${JSON.stringify(steps)}.`,
    });
  };

  return (
    <div
      className="self-stretch border border-stone-200 rounded-xl shadow-card bg-white p-3 flex flex-col gap-2.5"
    >
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-md flex items-center justify-center bg-ink text-accent shrink-0">
          <Filter size={13} strokeWidth={2} aria-hidden />
        </span>
        <span className="text-sm font-bold text-stone-900 flex-1">
          {input.title || "Segment builder"}
        </span>
      </div>
      <div className="flex flex-col gap-1.5">
        {steps.map((s, i) => {
          const width = STEP_WIDTHS[Math.min(i, STEP_WIDTHS.length - 1)];
          const bg = STEP_BG[Math.min(i, STEP_BG.length - 1)];
          return (
            <div
              key={i}
              className="flex items-center gap-2.5 px-2.5 py-2 rounded-md self-center transition-all duration-300"
              style={{ width, background: bg, alignSelf: i === 0 ? "stretch" : "center" }}
            >
              <div className="flex-1 flex flex-col gap-px min-w-0">
                <div className="text-xs font-medium text-stone-900 truncate">
                  {s.label || ""}
                </div>
                {s.filter_applied && (
                  <div className="text-[11px] text-stone-500 italic truncate">
                    {s.filter_applied}
                  </div>
                )}
              </div>
              <div className="font-mono font-bold text-sm text-ink tabular-nums shrink-0">
                {typeof s.count === "number" ? s.count.toLocaleString() : "—"}
              </div>
            </div>
          );
        })}
      </div>
      {input.save_as_segment_label && (
        <button
          onClick={handleSave}
          className="self-start px-3 py-1.5 rounded-md bg-action hover:bg-action-hover text-white text-xs font-semibold inline-flex items-center gap-1.5"
        >
          <Save size={12} aria-hidden /> Save as segment: "{input.save_as_segment_label}"
        </button>
      )}
    </div>
  );
}

registerRenderer<FunnelInput>({
  tool: "render_funnel",
  label: "Segment-builder funnel with save CTA",
  Component: FunnelCard,
});

export { FunnelCard };
